import {
  Alert,
  Box,
  Button,
  Grid2 as Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useStore } from "@tanstack/react-form";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import {
  useCreateMailCategoryMutation,
  useUpdateMailCategoryMutation,
} from "api/memberPortal/mailList/mailList.api";
import { MailCategory } from "api/memberPortal/mailList/mailList.types";
import { useAppForm } from "components/Forms/AppForm";
import { COLOR_OPTIONS } from "utils/color";

import {
  createCategorySchema,
  CreateCategorySchemaValues,
} from "./CreateCategory.schema";

interface CreateCategoryFormProps {
  onCancel: () => void;
  onCreate: (_data: MailCategory) => void;
  onUpdate: (_data: MailCategory) => void;
  selectedCategory?: MailCategory;
}

export default function CreateCategoryForm({
  onCancel,
  onCreate,
  onUpdate,
  selectedCategory,
}: CreateCategoryFormProps) {
  const [createCategory, { isError, error, isLoading }] =
    useCreateMailCategoryMutation();
  const [
    updateCategory,
    { isError: isUpdateError, error: updateError, isLoading: isUpdateLoading },
  ] = useUpdateMailCategoryMutation();

  const loading = isLoading || isUpdateLoading;

  const form = useAppForm({
    defaultValues: {
      id: selectedCategory?.id != null ? String(selectedCategory.id) : "",
      name: selectedCategory?.name ?? "",
      description: selectedCategory?.description ?? "",
      color: selectedCategory?.color ?? COLOR_OPTIONS[0],
    } as CreateCategorySchemaValues,

    validators: {
      onSubmit: createCategorySchema,
    },
    onSubmit: async ({ value }) => {
      if (!selectedCategory) {
        // Creating
        const { data } = await createCategory(value);
        if (data) {
          form.reset();
          onCreate(data);
        }
      } else {
        // Editing
        const { data } = await updateCategory(value);
        if (data) {
          form.reset();
          onUpdate(data);
        }
      }
    },
  });
  const isDirty = useStore(form.store, (state) => state.isDirty);

  const handleClose = () => {
    form.reset();
    onCancel();
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Grid container alignItems={"stretch"} gap={2} spacing={2}>
        <Grid size={{ sm: 12, md: 6 }}>
          <Stack spacing={2} sx={{ justifyContent: "space-between" }}>
            <Typography variant="overline" sx={{ color: "ink.light" }}>
              Details
            </Typography>
            <form.AppField name="name">
              {(field) => (
                <field.TextInput
                  label="Category Name"
                  required
                  disabled={loading}
                />
              )}
            </form.AppField>
            <form.AppField name="description">
              {(field) => (
                <field.TextInput
                  label="Description"
                  required
                  disabled={loading}
                />
              )}
            </form.AppField>
          </Stack>
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <Stack spacing={1}>
            <Typography variant="overline" sx={{ color: "ink.light" }}>
              Colors
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: 1,
                borderRadius: 3,
                borderColor: "glyph.dark",
                flex: 1,
                p: 2,
              }}
            >
              <form.AppField name="color">
                {(field) => <field.ColorButtonSelect disabled={loading} />}
              </form.AppField>
            </Box>
          </Stack>
        </Grid>
        <Grid
          size={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: "auto",
          }}
          gap={2}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={loading}
            disabled={!isDirty}
          >
            {`${selectedCategory ? "Edit" : "Create"} Category`}
          </Button>
        </Grid>
        <Grid size={6}>
          {(isError || isUpdateError) && (
            <Alert severity="error">
              {normalizeErrorMessages(error || updateError)}
            </Alert>
          )}
        </Grid>
      </Grid>
    </form>
  );
}
